import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { observer } from "mobx-react-lite";
import { Download, SlidersHorizontal, Search } from "lucide-react";
import {
  PageHeader,
  SearchableSelect,
  UnsavedChangesDialog,
} from "@/components/common";
import { useTran, useUnsavedChanges } from "@/hooks";
import {
  useTargetSetupStore,
  useTargetSetupUIStore,
} from "../hooks/useTargetSetupStore";
import {
  loadDisclosures,
  loadCompanyTargetFrequency,
  resetOrgListLoader,
} from "../operations/target-setup.ops";
import { resetAllStores } from "../stores";
import {
  CategoryGroup,
  ExportTargetSetupDialog,
  SetValueToAllDialog,
} from "../components";
import TargetSetupNavTabs from "../components/TargetSetupNavTabs";
import { DirtyRowsProvider, useDirtyRows } from "../hooks/useDirtyRowsContext";
import DisclosureListSkeleton from "../components/skeleton/DisclosureListSkeleton";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { loadOrgList } from "../operations/target-setup.ops";
import { loadReportingYears } from "@/features/reporting-boundary/operations/reporting-boundary.ops";
import { formatReportingYearPeriod } from "@/features/esg/utils/period-constants";
import { showToast } from "@/lib/utils/toast";
import { useAuthStore } from "@/features/auth";
import { buildReportingPeriodMonths } from "../domains/targetSetup";

const ALL = "All";
import PageTitle from "@/components/common/PageTitleBadge";
import { reportingYearStore } from "@/features/reporting-boundary/stores/reporting-year.store";

const normalizeFramework = (value) =>
  String(value ?? "")
    .trim()
    .toLowerCase();

const normalizeFrameworkList = (value) => {
  const collected = [];

  const addValue = (val) => {
    if (!val) return;
    if (Array.isArray(val)) {
      val.forEach(addValue);
      return;
    }
    if (typeof val === "object") {
      if (val.code) addValue(val.code);
      if (val.framework) addValue(val.framework);
      return;
    }
    if (typeof val === "string") {
      const parts = /[,/|]/.test(val) ? val.split(/[,/|]+/) : [val];
      for (const part of parts) {
        const normalized = normalizeFramework(part);
        if (normalized) collected.push(normalized);
      }
      return;
    }

    const normalized = normalizeFramework(val);
    if (normalized) collected.push(normalized);
  };

  addValue(value);
  return Array.from(new Set(collected));
};

const extractFrameworkCodes = (disclosure) => {
  const primary =
    disclosure?.framework ??
    disclosure?.frameworkCode ??
    disclosure?.framework_code;
  const primaryCodes = normalizeFrameworkList(primary);
  if (primaryCodes.length > 0) return primaryCodes;

  const fallback =
    disclosure?.frameworks ??
    disclosure?.frameworkCodes ??
    disclosure?.framework_codes;
  return normalizeFrameworkList(fallback);
};

const collectFrameworkOptionCodes = (value) => {
  const collected = [];

  const addValue = (val) => {
    if (!val) return;
    if (Array.isArray(val)) {
      val.forEach(addValue);
      return;
    }
    if (typeof val === "object") {
      addValue(val.code);
      addValue(val.framework);
      addValue(val.value);
      return;
    }
    if (typeof val === "string") {
      const parts = /[,/|]/.test(val) ? val.split(/[,/|]+/) : [val];
      parts.forEach((part) => {
        const code = String(part || "").trim();
        if (code) collected.push(code);
      });
      return;
    }

    const code = String(val ?? "").trim();
    if (code) collected.push(code);
  };

  addValue(value);
  return collected;
};

const extractFrameworkOptionCodes = (disclosure) => {
  const values = [
    disclosure?.framework,
    disclosure?.frameworkCode,
    disclosure?.framework_code,
    disclosure?.frameworks,
    disclosure?.frameworkCodes,
    disclosure?.framework_codes,
  ];

  const seen = new Set();
  const codes = [];

  values.flatMap(collectFrameworkOptionCodes).forEach((code) => {
    const key = normalizeFramework(code);
    if (!key || seen.has(key)) return;
    seen.add(key);
    codes.push(code);
  });

  return codes;
};

const extractDisclosureYears = (disclosure) => {
  const years = [];

  if (Array.isArray(disclosure?.masterDataYears)) {
    years.push(...disclosure.masterDataYears);
  }

  if (disclosure?.masterDataYear) {
    years.push(disclosure.masterDataYear);
  }

  if (Array.isArray(disclosure?.masterDataRecords)) {
    years.push(...disclosure.masterDataRecords.map((record) => record?.year));
  }

  return Array.from(
    new Set(
      years.map((year) => Number.parseInt(year, 10)).filter(Number.isFinite),
    ),
  );
};

const TargetSetupPageInner = observer(() => {
  const { t } = useTran();
  const { isJJSEA } = useAuthStore();
  const { hasDirtyRows } = useDirtyRows();
  const { showDialog, confirmDiscard, cancelDiscard } =
    useUnsavedChanges(hasDirtyRows);
  const {
    selectedFrameworks,
    frameworks,
    setSelectedFrameworks,
    disclosures,
    targetFrequencyLoaded,
    reportingYear,
    setReportingYear,
    searchDisclosure,
    setSearchDisclosure,
  } = useTargetSetupStore();
  const { loadingDisclosures } = useTargetSetupUIStore();
  const [isExportDialogOpen, setIsExportDialogOpen] = useState(false);
  const [companyHeaderTargetFrequency, setCompanyHeaderTargetFrequency] =
    useState(null);
  const { reportingYears } = reportingYearStore;
  const frameworkAllSelectedRef = useRef(false);

  // const [filters, setFilters] = useState({
  //   reportingYear: ALL,
  //   search: "",
  // });

  // Old Code
  // const yearOptions = useMemo(() => {
  //   const startYear = Number.parseInt(reportingBoundary?.startYear, 10);
  //   const endYear = Number.parseInt(reportingBoundary?.endYear, 10);
  //   const years = [];

  //   if (Number.isFinite(startYear) && Number.isFinite(endYear)) {
  //     const minYear = Math.min(startYear, endYear);
  //     const maxYear = Math.max(startYear, endYear);
  //     for (let year = maxYear; year >= minYear; year -= 1) {
  //       years.push({ label: String(year), value: String(year) });
  //     }
  //   }

  //   return [{ label: t("All"), value: "All" }, ...years];
  // }, [reportingBoundary?.startYear, reportingBoundary?.endYear, t]);

  // const { yearOptions, yearLabels, reportingPeriodsByYear } = useMemo(() => {
  //   const disclosureYears = new Set(
  //     disclosures.flatMap(extractDisclosureYears),
  //   );

  //   const reportingYearByStartYear = new Map(
  //     (reportingYears || [])
  //       .map((ry) => [Number.parseInt(ry?.startYear, 10), ry])
  //       .filter(([year]) => Number.isFinite(year)),
  //   );

  //   const options = Array.from(disclosureYears)
  //     .sort((a, b) => b - a)
  //     .map((year) => ({
  //       value: String(year),
  //       label: reportingYearByStartYear.has(year)
  //         ? formatReportingYearPeriod(reportingYearByStartYear.get(year))
  //         : String(year),
  //     }));

  //   return {
  //     yearOptions: [{ value: ALL, label: t("All") }, ...options],
  //     yearLabels: new Map(
  //       options.map((option) => [option.value, option.label]),
  //     ),
  //     reportingPeriodsByYear: new Map(
  //       (reportingYears || [])
  //         .map((ry) => [Number.parseInt(ry?.startYear, 10), ry])
  //         .filter(([year]) => Number.isFinite(year))
  //         .map(([year, reportingYear]) => [String(year), reportingYear]),
  //     ),
  //   };
  // }, [disclosures, reportingYears, t]);

  const reportingYearByStartYear = useMemo(
    () =>
      new Map(
        (reportingYears || [])
          .map((ry) => [Number.parseInt(ry.startYear, 10), ry])
          .filter(([year]) => Number.isFinite(year)),
      ),
    [reportingYears],
  );

  const yearOptions = useMemo(() => {
    const options = [...reportingYearByStartYear.entries()]
      .sort((a, b) => b[0] - a[0])
      .map(([year, ry]) => ({
        value: String(year),
        label: formatReportingYearPeriod(ry),
      }));

    return [{ value: ALL, label: t("All") }, ...options];
  }, [reportingYearByStartYear, t]);

  const yearLabels = useMemo(
    () =>
      new Map(
        yearOptions
          .filter((o) => o.value !== ALL)
          .map((o) => [o.value, o.label]),
      ),
    [yearOptions],
  );

  const reportingPeriodsByYear = useMemo(
    () =>
      new Map(
        [...reportingYearByStartYear.entries()].map(([year, ry]) => [
          String(year),
          ry,
        ]),
      ),
    [reportingYearByStartYear],
  );

  const exportYearOptions = useMemo(() => {
    return (reportingYears || [])
      .slice()
      .sort((a, b) => b.startYear - a.startYear)
      .map((ry) => ({
        label: formatReportingYearPeriod(ry),
        value: String(ry.startYear),
      }));
  }, [reportingYears]);

  const getCardyearLabel = useCallback(
    (year) => {
      const found = yearLabels.get(String(year));
      return found ?? String(year);
    },
    [yearLabels],
  );

  const getReportingPeriodMonths = useCallback(
    (year) =>
      buildReportingPeriodMonths(
        reportingPeriodsByYear.get(String(year)),
        year,
      ),
    [reportingPeriodsByYear],
  );

  // const exportYearOptions = useMemo(
  //   () => yearOptions.filter((option) => option.value !== ALL),
  //   [yearOptions],
  // );

  const exportDefaultYear = useMemo(() => {
    if (reportingYear !== "All") {
      return String(reportingYear);
    }
    if (!reportingYears?.length) {
      return exportYearOptions[0]?.value || "";
    }

    const latestReportingYear = [...reportingYears].sort((a, b) => {
      // compare by reporting period end date
      const aDate = new Date(a.endYear, a.endMonth - 1);
      const bDate = new Date(b.endYear, b.endMonth - 1);

      return bDate - aDate;
    })[0];

    return String(latestReportingYear.startYear);
  }, [reportingYears, exportYearOptions, reportingYear]);

  useEffect(() => {
    loadDisclosures({ initFrameworks: true });
    loadOrgList();
    void loadCompanyTargetFrequency().then(setCompanyHeaderTargetFrequency);
    loadReportingYears();
  }, []);

  const frameworkLabelByCode = useMemo(
    () =>
      new Map(
        frameworks
          .map((fw) => {
            const code = fw?.code || fw?.framework || fw?.value || fw?.id;
            if (!code) return null;
            return [
              normalizeFramework(code),
              String(fw?.name || fw?.label || code).trim(),
            ];
          })
          .filter(Boolean),
      ),
    [frameworks],
  );

  const yearFilteredDisclosures = useMemo(() => {
    if (reportingYear === ALL) {
      return disclosures;
    }

    return disclosures.filter((disclosure) =>
      (disclosure.masterDataYears ?? []).some(
        (year) => String(year) === String(reportingYear),
      ),
    );
  }, [disclosures, reportingYear]);

  const frameworkOptions = useMemo(() => {
    const seen = new Set();
    const disclosureFrameworks = yearFilteredDisclosures
      .flatMap(extractFrameworkOptionCodes)
      .filter((code) => {
        const key = normalizeFramework(code);

        if (!key || seen.has(key)) return false;

        seen.add(key);
        return true;
      })
      .sort((a, b) => {
        const labelA = frameworkLabelByCode.get(normalizeFramework(a)) || a;

        const labelB = frameworkLabelByCode.get(normalizeFramework(b)) || b;

        return labelA.localeCompare(labelB);
      });
    const options = disclosureFrameworks.map((framework) => ({
      value: framework,
      label:
        frameworkLabelByCode.get(normalizeFramework(framework)) || framework,
    }));

    // One framework: remove All option
    if (options.length === 1) {
      return options;
    }

    return [{ label: t("All"), value: ALL }, ...options];
  }, [yearFilteredDisclosures, frameworkLabelByCode, t]);

  // const frameworkOptions = useMemo(() => {
  //   const seen = new Set();

  //   const years =
  //     reportingYear === ALL
  //       ? (reportingYears ?? [])
  //       : (reportingYears ?? []).filter(
  //           (ry) => String(ry.startYear) === String(reportingYear),
  //         );

  //   const options = years
  //     .flatMap((ry) => ry.frameworkCodes || [])
  //     .filter((code) => {
  //       const key = normalizeFramework(code);

  //       if (seen.has(key)) return false;

  //       seen.add(key);
  //       return true;
  //     })
  //     .map((code) => ({
  //       value: code,
  //       label: frameworkLabelByCode.get(normalizeFramework(code)) || code,
  //     }))
  //     .sort((a, b) => a.label.localeCompare(b.label));

  //   return [{ value: ALL, label: t("All") }, ...options];
  // }, [reportingYears, reportingYear, frameworkLabelByCode, t]);

  const selectedFramework = selectedFrameworks[0] ?? ALL;

  // Validate selected reporting year
  useEffect(() => {
    if (reportingYear === ALL) return;

    const reportingYearExists = yearOptions.some(
      (option) => String(option.value) === String(reportingYear),
    );

    if (!reportingYearExists) {
      setFilters((prev) => ({
        ...prev,
        reportingYear: ALL,
      }));
    }
  }, [reportingYear, yearOptions]);

  // Sync framework selection when reporting year changes
  useEffect(() => {
    const availableFrameworks = frameworkOptions.filter(
      (option) => option.value !== ALL,
    );
    const nonEmptySelectedFrameworks = selectedFrameworks.filter(
      (framework) => framework && framework !== ALL,
    );

    if (nonEmptySelectedFrameworks.length !== selectedFrameworks.length) {
      setSelectedFrameworks(nonEmptySelectedFrameworks);
      return;
    }

    // No available frameworks
    if (availableFrameworks.length === 0) {
      if (selectedFrameworks.length > 0) {
        setSelectedFrameworks([]);
      }

      return;
    }

    // JJSEA user: default-select JJ-Group when nothing is selected
    if (
      isJJSEA &&
      selectedFrameworks.length === 0 &&
      !frameworkAllSelectedRef.current
    ) {
      const jjGroup = availableFrameworks.find(
        (option) => option.value === "JJ-Group",
      );

      if (jjGroup) {
        setSelectedFrameworks([jjGroup.value]);
        return;
      }
    }

    // One available framework: auto-select it
    if (availableFrameworks.length === 1) {
      const onlyFramework = availableFrameworks[0].value;

      if (
        selectedFrameworks.length !== 1 ||
        selectedFrameworks[0] !== onlyFramework
      ) {
        setSelectedFrameworks([onlyFramework]);
      }

      return;
    }

    // Remove frameworks that are no longer available
    const availableCodes = new Set(
      availableFrameworks.map((option) => option.value),
    );

    const validSelectedFrameworks = selectedFrameworks.filter((code) =>
      availableCodes.has(code),
    );

    if (validSelectedFrameworks.length !== selectedFrameworks.length) {
      setSelectedFrameworks(validSelectedFrameworks);
    }
  }, [
    frameworkOptions,
    selectedFrameworks,
    isJJSEA,
    setSelectedFrameworks,
    reportingYear,
  ]);
  useEffect(() => {
    return () => {
      resetOrgListLoader();
      resetAllStores();
    };
  }, []);

  const filteredDisclosures = useMemo(() => {
    // Flatten: one row per year. Override the merged top-level
    // isAllOrganization / isCompanySelected / org list with the per-year
    // values from masterDataRecords. The backend's top-level flags are a
    // .some()-merge across all years, which silently mis-represents the
    // org scope of any year that differs from the others.
    let result = disclosures.flatMap((d) => {
      const years = d.masterDataYears || [];
      if (years.length === 0) return [d];
      const idToName = new Map();
      (d.organizationIds || []).forEach((id, i) =>
        idToName.set(String(id), d.organizations?.[i]),
      );
      return years.map((y) => {
        const yearRecord = d.masterDataRecords?.find((r) => r.year === y);
        if (!yearRecord) {
          return { ...d, masterDataYear: y, masterDataYears: [y] };
        }
        const yearOrgIds = (yearRecord.organizationIds || []).map(String);
        const yearOrgs = yearOrgIds
          .map((id) => idToName.get(id))
          .filter(Boolean);
        return {
          ...d,
          masterDataYear: y,
          masterDataYears: [y],
          isAllOrganization: Boolean(yearRecord.isAllOrganization),
          isCompanySelected: Boolean(yearRecord.isCompanySelected),
          organizationIds: yearOrgIds,
          organizations: yearOrgs,
        };
      });
    });

    if (selectedFrameworks.length > 0) {
      const selected = new Set(
        selectedFrameworks.map(normalizeFramework).filter(Boolean),
      );
      result = result.filter((disclosure) => {
        const frameworks = extractFrameworkCodes(disclosure);
        if (frameworks.length === 0) return false;
        return frameworks.some((fw) => selected.has(fw));
      });
    }

    if (reportingYear !== "All") {
      const selectedYear = String(reportingYear);
      result = result.filter((d) => String(d.masterDataYear) === selectedYear);
    }
    const search = searchDisclosure.trim().toLowerCase();
    if (!search) return result;

    return result.filter((disclosure) => {
      const code = String(disclosure.code || "").toLowerCase();
      const title = String(disclosure.title || "").toLowerCase();
      return code.includes(search) || title.includes(search);
    });
  }, [disclosures, reportingYear, searchDisclosure, selectedFrameworks]);

  const categories = useMemo(() => {
    const order = ["General", "Environmental", "Social", "Governance"];
    const grouped = {};
    for (const d of filteredDisclosures) {
      const cat = d.category || "Other";
      if (!grouped[cat]) grouped[cat] = [];
      grouped[cat].push(d);
    }
    return order
      .filter((key) => grouped[key]?.length > 0)
      .map((key) => ({ key, items: grouped[key] }));
  }, [filteredDisclosures]);

  const resetFilters = () => {
    frameworkAllSelectedRef.current = false;
    setFilters({
      reportingYear: ALL,
      search: "",
    });
    setSelectedFrameworks([]);
  };

  const frequencyBadge = companyHeaderTargetFrequency
    ? companyHeaderTargetFrequency === "monthly"
      ? t("Monthly")
      : t("Yearly")
    : null;

  const pageTitle = (
    <PageTitle title={t("Target Setup")} frequencyBadge={frequencyBadge} />
  );

  return (
    <div className="flex-1 min-h-0 flex flex-col gap-y-4">
      <PageHeader
        title={pageTitle}
        description={t(
          "View assigned metric disclosures grouped by category and enter target data per organization.",
        )}
        icon={SlidersHorizontal}
        actions={
          <div className="flex items-center gap-3">
            <Button
              type="button"
              size="sm"
              onClick={() => setIsExportDialogOpen(true)}
              disabled={loadingDisclosures || exportYearOptions.length === 0}
            >
              <Download className="mr-2 h-4 w-4" />
              {t("Excel Export")}
            </Button>
            <TargetSetupNavTabs />
          </div>
        }
      />
      {/* Temporary Hidden */}
      {/* <ReportPeriodAndFrameworks
      <ReportPeriodAndFrameworks
        reportingPeriodLabel={reportingPeriodLabel}
        frameworksSummary={frameworksSummary}
      /> */}

      <div className="grid grid-cols-1 items-end gap-3 lg:grid-cols-12">
        <div className="space-y-1.5 lg:col-span-3">
          <p className="text-sm font-medium text-foreground">
            {t("Reporting Year")}
          </p>
          <SearchableSelect
            // value={filters.reportingYear}
            // onChange={(value) =>
            //   setFilters((prev) => ({
            //     ...prev,
            //     reportingYear: value || ALL,
            //   }))
            // }
            value={reportingYear}
            onChange={(value) => setReportingYear(value || "All")}
            options={yearOptions}
            placeholder={t("Target Year")}
          />
        </div>
        <div className="space-y-1.5 lg:col-span-3">
          <p className="text-sm font-medium text-foreground">
            {t("Framework")}
          </p>
          <SearchableSelect
            options={frameworkOptions}
            value={selectedFramework}
            onChange={(value) => {
              frameworkAllSelectedRef.current = value === ALL;
              const nextValue = value && value !== ALL ? [value] : [];
              setSelectedFrameworks(nextValue);
            }}
            placeholder={t("All Frameworks")}
            searchPlaceholder={t("Search frameworks...")}
          />
        </div>
        <div className="space-y-1.5 lg:col-span-3">
          <p className="text-sm font-medium text-foreground">{t("Search")}</p>
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            {/* <Input
              value={filters.search}
              onChange={(event) => {
                const { value } = event.target;
                setFilters((prev) => ({
                  ...prev,
                  search: value,
                }));
              }}
              placeholder={t("Search by code or title...")}
              className="pl-9"
            /> */}
            <Input
              value={searchDisclosure}
              onChange={(event) => setSearchDisclosure(event.target.value)}
              placeholder={t("Search by code or title...")}
              className="pl-9"
            />
          </div>
        </div>
        {!isJJSEA && (
          <div className="space-y-1.5 lg:col-span-1">
            <Button
              type="button"
              variant="ghost"
              onClick={resetFilters}
              className="h-auto px-0 text-sm text-destructive hover:text-destructive/80"
            >
              {t("Clear All Filters")}
            </Button>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 min-h-0 overflow-y-auto">
        {loadingDisclosures || !targetFrequencyLoaded ? (
          <DisclosureListSkeleton />
        ) : categories.length > 0 ? (
          <div className="space-y-4">
            {categories.map(({ key, items }) => (
              <CategoryGroup
                key={key}
                category={key}
                disclosures={items}
                getCardyearLabel={getCardyearLabel}
                getReportingPeriodMonths={getReportingPeriodMonths}
              />
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <p className="text-sm text-muted-foreground">
              {t("No disclosures found")}
            </p>
          </div>
        )}
      </div>

      <SetValueToAllDialog
        getReportingPeriodMonths={getReportingPeriodMonths}
      />
      <ExportTargetSetupDialog
        open={isExportDialogOpen}
        onOpenChange={setIsExportDialogOpen}
        defaultYear={exportDefaultYear}
        defaultFramework={selectedFrameworks[0] || ""}
        yearOptions={exportYearOptions}
        reportingYears={reportingYears}
        availableFrameworks={frameworks}
      />
      <UnsavedChangesDialog
        open={showDialog}
        onConfirm={confirmDiscard}
        onCancel={cancelDiscard}
      />
    </div>
  );
});

const TargetSetupPage = () => (
  <DirtyRowsProvider>
    <TargetSetupPageInner />
  </DirtyRowsProvider>
);

export default TargetSetupPage;
